import { ensureNever } from "../../../../Common/TypingUtils";
import { BindingPath } from "../../Engine/Core/CoreTypes";
import { isLessOrEqualStringDates } from "../DateHelpers";
import { wrapDecimalOrUndefined } from "../AutocalcCommonFunctions";

import {
    CompareWithConstantCondition,
    FilterAndExpression,
    FilterByInstancesExpression,
    FilterEndDateExpression,
    FilterEqualExpression,
    FilterExpression,
    FilterGreaterOrEqualExpression,
    FilterLessOrEqualExpression,
    FilterOrExpression,
    FilterStartDateExpression,
    FilterSubstringExpression,
    FilterUnaryNotExpression,
    OperatorType,
} from "./FilterExpression";

type GetModelValuesFn = (dataPath: BindingPath) => string[];
type GetInstanceFn = (dataPath: BindingPath) => string;

export type ConditionalFunction = (getModelValuesFn: GetModelValuesFn, getInstanceFn: GetInstanceFn) => boolean;

export function compileFilterExpression(expression: FilterExpression): ConditionalFunction {
    const compileVisitor = new CompileFilterExpressionTransduceVisitor();
    return compileVisitor.transformExpression(expression);
}

export abstract class FilterExpressionTransduceVisitor<T> {
    public transformExpression(expression: FilterExpression): T {
        switch (expression.operator) {
            case OperatorType.Or:
                return this.transformOrExpression(expression);
            case OperatorType.And:
                return this.transformAndExpression(expression);
            case OperatorType.Equal:
                return this.transformEqualExpression(expression);
            case OperatorType.StartDate:
                return this.transformStartDateExpression(expression);
            case OperatorType.EndDate:
                return this.transformEndDateExpression(expression);
            case OperatorType.Substring:
                return this.transformSubstringExpression(expression);
            case OperatorType.UnaryNot:
                return this.transformUnaryNotExpression(expression);
            case OperatorType.ByInstances:
                return this.transformByInstancesExpression(expression);
            case OperatorType.LessOrEqual:
                return this.transformLessOrEqualExpression(expression);
            case OperatorType.GreaterOrEqual:
                return this.transformGreaterOrEqualExpression(expression);
            default:
                ensureNever(expression);
                return this.createDefaultResult();
        }
    }

    protected abstract createDefaultResult(): T;
    protected abstract transformOrExpression(expression: FilterOrExpression): T;
    protected abstract transformAndExpression(expression: FilterAndExpression): T;
    protected abstract transformEqualExpression(expression: FilterEqualExpression): T;
    protected abstract transformStartDateExpression(expression: FilterStartDateExpression): T;
    protected abstract transformEndDateExpression(expression: FilterEndDateExpression): T;
    protected abstract transformSubstringExpression(expression: FilterSubstringExpression): T;
    protected abstract transformByInstancesExpression(expression: FilterByInstancesExpression): T;
    protected abstract transformUnaryNotExpression(expression: FilterUnaryNotExpression): T;
    protected abstract transformLessOrEqualExpression(expression: FilterLessOrEqualExpression): T;
    protected abstract transformGreaterOrEqualExpression(expression: FilterGreaterOrEqualExpression): T;
}

class CompileFilterExpressionTransduceVisitor extends FilterExpressionTransduceVisitor<ConditionalFunction> {
    protected createDefaultResult(): ConditionalFunction {
        return () => true;
    }

    protected transformOrExpression(expression: FilterOrExpression): ConditionalFunction {
        const conditions = expression.conditions.map(x => this.transformExpression(x));

        return (getModelValuesFn, GetInstanceFn) =>
            conditions.some(condition => condition(getModelValuesFn, GetInstanceFn));
    }

    protected transformAndExpression(expression: FilterAndExpression): ConditionalFunction {
        const conditions = expression.conditions.map(x => this.transformExpression(x));

        return (getModelValuesFn, GetInstanceFn) =>
            conditions.every(condition => condition(getModelValuesFn, GetInstanceFn));
    }

    protected transformEqualExpression(expression: FilterEqualExpression): ConditionalFunction {
        const conditionValue = this.transformConditionValueExpression(expression.condition);

        return (getModelValuesFn: GetModelValuesFn) =>
            getModelValuesFn(expression.condition.dataPath).some(x => x === conditionValue);
    }

    protected transformStartDateExpression(expression: FilterStartDateExpression): ConditionalFunction {
        const conditionValue = this.transformConditionValueExpression(expression.condition);

        return (getModelValuesFn: GetModelValuesFn) =>
            getModelValuesFn(expression.condition.dataPath).some(x => isLessOrEqualStringDates(conditionValue, x));
    }

    protected transformEndDateExpression(expression: FilterEndDateExpression): ConditionalFunction {
        const conditionValue = this.transformConditionValueExpression(expression.condition);

        return (getModelValuesFn: GetModelValuesFn) =>
            getModelValuesFn(expression.condition.dataPath).some(x => isLessOrEqualStringDates(x, conditionValue));
    }

    protected transformSubstringExpression(expression: FilterSubstringExpression): ConditionalFunction {
        const conditionValue = this.transformConditionValueExpression(expression.condition);

        return (getModelValuesFn: GetModelValuesFn) =>
            getModelValuesFn(expression.condition.dataPath).some(x =>
                stringNormalize(x).includes(stringNormalize(conditionValue))
            );
    }

    protected transformUnaryNotExpression(expression: FilterUnaryNotExpression): ConditionalFunction {
        const condition = this.transformExpression(expression.condition);

        return (getModelValuesFn: GetModelValuesFn, getPathsFn) => !condition(getModelValuesFn, getPathsFn);
    }

    protected transformConditionValueExpression(condition: CompareWithConstantCondition): string {
        return condition.value;
    }

    protected transformByInstancesExpression(expression: FilterByInstancesExpression): ConditionalFunction {
        return (_, getInstanceFn) => {
            const instance = getInstanceFn(expression.condition.dataPath);
            return expression.condition.instances.some(x => x === instance);
        };
    }

    protected transformLessOrEqualExpression(expression: FilterLessOrEqualExpression): ConditionalFunction {
        const conditionValue = wrapDecimalOrUndefined(this.transformConditionValueExpression(expression.condition));
        return (getModelValuesFn: GetModelValuesFn) =>
            getModelValuesFn(expression.condition.dataPath).some(x => {
                const typedDataPathValue = wrapDecimalOrUndefined(x);
                return conditionValue != undefined && typedDataPathValue != undefined
                    ? typedDataPathValue.lte(conditionValue)
                    : false;
            });
    }

    protected transformGreaterOrEqualExpression(expression: FilterGreaterOrEqualExpression): ConditionalFunction {
        const conditionValue = wrapDecimalOrUndefined(this.transformConditionValueExpression(expression.condition));
        return (getModelValuesFn: GetModelValuesFn) =>
            getModelValuesFn(expression.condition.dataPath).some(x => {
                const typedDataPathValue = wrapDecimalOrUndefined(x);
                return conditionValue != undefined && typedDataPathValue != undefined
                    ? typedDataPathValue.gte(conditionValue)
                    : false;
            });
    }
}

function stringNormalize(str: string): string {
    return str.trim().toLocaleLowerCase();
}
